/**
 * ReportScheduleConfig — UI for configuring automatic monthly report sending.
 * Stores schedule in Supabase `report_schedules` table (graceful fallback if table missing).
 * Allows setting: day of month, time, include/exclude filter, active toggle.
 */
import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { supabase } from "../../services/core.js";
import { logAudit } from "../../supabaseClient.js";
import { useToast } from "../../components/Toast.jsx";
import { RADIUS } from "../../shared/theme.js";

const DEFAULT_SCHEDULE = {
  enabled: false,
  day_of_month: 5,
  hour: 9,
  minute: 0,
  include_all: true,
  excluded_clients: [],
};

export default function ReportScheduleConfig({ T }) {
  const toast = useToast();
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tableExists, setTableExists] = useState(true);

  // ── Load existing schedule ──
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from("report_schedules")
          .select("*")
          .eq("schedule_type", "monthly_report")
          .maybeSingle();

        if (error?.code === "42P01") {
          // Table doesn't exist yet — show config UI anyway with defaults
          setTableExists(false);
          setLoading(false);
          return;
        }
        if (error) throw error;
        if (data) {
          setSchedule({
            enabled: data.enabled ?? false,
            day_of_month: data.day_of_month ?? 5,
            hour: data.hour ?? 9,
            minute: data.minute ?? 0,
            include_all: data.include_all ?? true,
            excluded_clients: data.excluded_clients ?? [],
          });
        }
      } catch {
        // Graceful fallback — table may not exist yet
        setTableExists(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Save schedule ──
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (!tableExists) {
        toast("Tabela report_schedules não encontrada. Execute a migration primeiro.", "warning");
        setSaving(false);
        return;
      }

      const payload = {
        schedule_type: "monthly_report",
        enabled: schedule.enabled,
        day_of_month: schedule.day_of_month,
        hour: schedule.hour,
        minute: schedule.minute,
        include_all: schedule.include_all,
        excluded_clients: schedule.excluded_clients,
        cron_expression: `${schedule.minute} ${schedule.hour} ${schedule.day_of_month} * *`,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("report_schedules")
        .upsert(payload, { onConflict: "schedule_type" });

      if (error) throw error;

      await logAudit("report_schedule_updated", {
        enabled: schedule.enabled,
        day: schedule.day_of_month,
        hour: schedule.hour,
      }).catch(() => {});

      toast("Agendamento salvo com sucesso!", "success");
    } catch (err) {
      toast(`Erro ao salvar: ${err.message}`, "error");
    } finally {
      setSaving(false);
    }
  }, [schedule, tableExists, toast]);

  const update = (key, val) => setSchedule((s) => ({ ...s, [key]: val }));

  if (loading) {
    return (
      <div style={{ padding: 20, color: T.muted, fontSize: 13 }}>Carregando configuração...</div>
    );
  }

  const cronStr = `${schedule.minute} ${schedule.hour} ${schedule.day_of_month} * *`;
  const nextRunDesc = (() => {
    const now = new Date();
    const next = new Date(
      now.getFullYear(),
      now.getMonth(),
      schedule.day_of_month,
      schedule.hour,
      schedule.minute
    );
    if (next <= now) next.setMonth(next.getMonth() + 1);
    return next.toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  })();

  const inputStyle = {
    padding: "7px 12px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.text,
    fontSize: 13,
    width: 80,
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: T.sub,
    marginBottom: 4,
    display: "block",
  };

  return (
    <div
      style={{
        padding: "20px 24px",
        background: T.card,
        borderRadius: RADIUS.md,
        border: `1px solid ${T.border}`,
      }}
    >
      {/* Header with toggle */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>⏰ Envio Automático</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
            Relatório mensal enviado automaticamente para todos os clientes com e-mail
          </div>
        </div>
        <button
          onClick={() => update("enabled", !schedule.enabled)}
          style={{
            padding: "6px 16px",
            borderRadius: 20,
            border: "none",
            background: schedule.enabled ? "#16A34A" : "#94A3B8",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            cursor: "pointer",
            transition: "background 0.2s ease",
            minWidth: 80,
          }}
        >
          {schedule.enabled ? "Ativo" : "Inativo"}
        </button>
      </div>

      {/* Config fields */}
      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: 16,
          opacity: schedule.enabled ? 1 : 0.5,
          pointerEvents: schedule.enabled ? "auto" : "none",
        }}
      >
        <div>
          <label style={labelStyle}>Dia do mês</label>
          <select
            value={schedule.day_of_month}
            onChange={(e) => update("day_of_month", Number(e.target.value))}
            style={inputStyle}
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Hora</label>
          <select
            value={schedule.hour}
            onChange={(e) => update("hour", Number(e.target.value))}
            style={inputStyle}
          >
            {Array.from({ length: 24 }, (_, i) => i).map((h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}:00
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Destinatários</label>
          <select
            value={schedule.include_all ? "all" : "filter"}
            onChange={(e) => update("include_all", e.target.value === "all")}
            style={{ ...inputStyle, width: 200 }}
          >
            <option value="all">Todos com e-mail</option>
            <option value="filter">Personalizado (em breve)</option>
          </select>
        </div>
      </div>

      {/* Preview */}
      {schedule.enabled && (
        <div
          style={{
            padding: "10px 14px",
            background: `${T.info || "#2563EB"}08`,
            borderRadius: RADIUS.sm,
            border: `1px solid ${T.info || "#2563EB"}20`,
            marginBottom: 16,
            fontSize: 12,
            color: T.sub,
            lineHeight: 1.7,
          }}
        >
          <strong style={{ color: T.text }}>Resumo:</strong> Relatório enviado todo dia{" "}
          <strong>{schedule.day_of_month}</strong> às{" "}
          <strong>
            {String(schedule.hour).padStart(2, "0")}:{String(schedule.minute).padStart(2, "0")}
          </strong>{" "}
          para {schedule.include_all ? "todos os clientes com e-mail" : "clientes selecionados"}.
          <br />
          <span style={{ color: T.muted }}>
            Cron:{" "}
            <code style={{ background: T.bg, padding: "1px 5px", borderRadius: 3 }}>{cronStr}</code>
            {" · "}Próximo envio: {nextRunDesc}
          </span>
        </div>
      )}

      {/* Migration notice */}
      {!tableExists && (
        <div
          style={{
            padding: "10px 14px",
            background: "#FEF3C7",
            borderRadius: RADIUS.sm,
            border: "1px solid #FDE68A",
            marginBottom: 16,
            fontSize: 12,
            color: "#92400E",
          }}
        >
          ⚠ Tabela <code>report_schedules</code> não encontrada no banco. Execute a migration para
          habilitar o agendamento persistente. As configurações atuais são apenas visuais.
        </div>
      )}

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: "9px 24px",
          borderRadius: RADIUS.sm,
          border: "none",
          background: T.text,
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          cursor: saving ? "wait" : "pointer",
          opacity: saving ? 0.7 : 1,
        }}
      >
        {saving ? "Salvando..." : "💾 Salvar Agendamento"}
      </button>
    </div>
  );
}

ReportScheduleConfig.propTypes = {
  T: PropTypes.object.isRequired,
};
